import React, { useState, useRef, useEffect, useCallback, ChangeEvent, FormEvent } from 'react';
import cn from 'classnames';

import Strings from '../../Strings';
import { TextInput } from '../../components/Inputs/TextInput';
import { PasswordInput } from '../../components/Inputs/PasswordInput';
import { Button, BUTTON_TYPE_BLUE } from '../../components/Buttons/Button';
import { Errors } from '../../components/Login/Errors/Errors';
import { ButtonRow } from '../../components/Login/ButtonRow/ButtonRow';
import { Text } from '../../components/Login/Text/Text';
import { PasswordRequirementsDisplay } from '../../components/PasswordRequirements';
import {
  validateCredential,
  validatePassword,
  getCredentialTypeLabel,
  CredentialType,
  CredentialValidationResult,
  PasswordValidationResult,
  PasswordStrength,
} from '../../utils/validation';
import styles from '../../components/PasswordRequirements/PasswordRequirements.module.scss';

interface LoginFormState {
  username: string;
  password: string;
}

interface LoginFormProps {
  onForgotClick: () => void;
  onBackToLogin?: () => void;
  state: LoginFormState;
  errors: string[];
  onLogin: (event: FormEvent) => void;
  isEnabled: boolean;
  isLoading: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSSOLogin?: () => void;
  ssoResolverLoading?: boolean;
  ssoOnly?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onForgotClick,
  onBackToLogin,
  state,
  errors,
  onLogin,
  onChange,
  isEnabled,
  isLoading,
  onSSOLogin,
  ssoResolverLoading,
  ssoOnly = false,
}) => {
  const textInputRef = useRef<HTMLInputElement>(null);

  // Validation state
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  // Credential validation
  const [credentialValidation, setCredentialValidation] = useState<CredentialValidationResult>({
    type: CredentialType.Username,
    isValid: false,
  });

  // Password validation
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult>({
    requirements: {
      minLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecial: false,
    },
    strength: PasswordStrength.VeryWeak,
    strengthLabel: 'Very Weak',
    isValid: false,
  });

  useEffect(() => {
    if (textInputRef.current) {
      textInputRef.current.focus();
    }
  }, []);

  // Validate credential when username changes
  useEffect(() => {
    if (state.username) {
      const result = validateCredential(state.username);
      setCredentialValidation(result);
    } else {
      setCredentialValidation({
        type: CredentialType.Username,
        isValid: false,
      });
    }
  }, [state.username]);

  // Validate password when it changes
  useEffect(() => {
    if (state.password) {
      const result = validatePassword(state.password);
      setPasswordValidation(result);
    } else {
      setPasswordValidation({
        requirements: {
          minLength: false,
          hasUppercase: false,
          hasLowercase: false,
          hasNumber: false,
          hasSpecial: false,
        },
        strength: PasswordStrength.VeryWeak,
        strengthLabel: 'Very Weak',
        isValid: false,
      });
    }
  }, [state.password]);

  const handleUsernameBlur = useCallback(() => {
    setUsernameTouched(true);
  }, []);

  const handlePasswordBlur = useCallback(() => {
    setPasswordTouched(true);
    setPasswordFocused(false);
  }, []);

  const handlePasswordFocus = useCallback(() => {
    setPasswordFocused(true);
  }, []);

  // Show credential type feedback only when valid and touched
  const showCredentialFeedback = usernameTouched && state.username.length > 0;
  const credentialFeedbackText = credentialValidation.isValid
    ? getCredentialTypeLabel(credentialValidation.type)
    : credentialValidation.error;
  const credentialIsValid = credentialValidation.isValid;

  // Show password requirements when password field is focused or has content
  const showPasswordRequirements = passwordFocused || (passwordTouched && state.password.length > 0);
  const ssoOnlyEnabled = state.username.trim().length > 0;
  const handleSubmit = useCallback((event: FormEvent) => {
    if (ssoOnly) {
      event.preventDefault();
      if (onSSOLogin) {
        onSSOLogin();
      }
      return;
    }

    onLogin(event);
  }, [onLogin, onSSOLogin, ssoOnly]);

  return (
    <form onSubmit={handleSubmit}>
      <TextInput
        onChange={onChange}
        onBlur={handleUsernameBlur}
        name="username"
        autoComplete="off"
        placeholder={Strings.userNameEmail}
        value={state.username}
        refLink={textInputRef}
        align="left"
        data-testid="username"
        error={usernameTouched && !credentialValidation.isValid && state.username.length > 0 ? credentialValidation.error : undefined}
      />

      {ssoOnly && (
        <Text>
          {Strings.enterEmailOrUsername}
        </Text>
      )}

      {/* {showCredentialFeedback && credentialIsValid && (
        <div className={cn(styles.credentialFeedback, styles.valid)}>
          {credentialFeedbackText}
        </div>
      )} */}

      {!ssoOnly && (
        <PasswordInput
          onChange={onChange}
          onBlur={handlePasswordBlur}
          onFocus={handlePasswordFocus}
          name="password"
          autoComplete="off"
          align="left"
          value={state.password}
          placeholder={Strings.password}
          data-testid="password"
        />
      )}

      {/* <PasswordRequirementsDisplay
        requirements={passwordValidation.requirements}
        strength={passwordValidation.strength}
        strengthLabel={passwordValidation.strengthLabel}
        show={showPasswordRequirements}
      /> */}

      <Errors errors={errors} />

      {!ssoOnly && (
        <ButtonRow
          left={
            <Button type="button" onClick={onForgotClick} disabled={isLoading}>
              {Strings.forgotPassword}
            </Button>
          }
          right={
            <Button buttonType={BUTTON_TYPE_BLUE} disabled={!isEnabled || isLoading} type="submit" data-testid="submit">
              {Strings.login}
            </Button>
          }
        />
      )}

      {onSSOLogin && (
        <ButtonRow
          left={
            ssoOnly ? (
              <Button type="button" onClick={onBackToLogin} disabled={isLoading || ssoResolverLoading}>
                {Strings.back}
              </Button>
            ) : undefined
          }
          right={
            <Button
              type="button"
              buttonType={BUTTON_TYPE_BLUE}
              disabled={isLoading || ssoResolverLoading || (ssoOnly && !ssoOnlyEnabled)}
              onClick={onSSOLogin}
              data-testid="sso-login"
            >
              {ssoResolverLoading ? `${Strings.checking}` : `${Strings.continueWithSSO}`}
            </Button>
          }
        />
      )}
    </form>
  );
};

export default LoginForm;
